/**
 * Khối "Xét quý" ở thẻ Bảng tổng tháng (QĐ 0483, CEO 2a 29/10/2026): chỉ ở
 * tháng trả (01, 04, 07, 10) — từng tiểu đội người xem được thấy, số do máy
 * chủ tính (`salesteam.ComputeQuarter`), màn không tự tính lại.
 */
import { EmptyState } from "@/components/EmptyState";
import { formatVND } from "@/lib/format";
import type { SalesTeamQuarter } from "../api";
import { monthText, percentFromBp } from "../labels";
import { SquadMark } from "./marks";

/** ["2026-11", "2026-12"] ⇒ "Tháng 11 + 12/2026"; khác năm thì ghi đủ từng tháng. */
function monthsText(months: string[]): string {
  const years = new Set(months.map((m) => m.slice(0, 4)));
  if (years.size === 1) return `Tháng ${months.map((m) => m.slice(5)).join(" + ")}/${[...years][0]}`;
  return `Tháng ${months.map(monthText).join(" + ")}`;
}

function Row({ label, value, strong, muted }: { label: string; value: string; strong?: boolean; muted?: boolean }) {
  return (
    <div className={`flex justify-between gap-3 ${muted ? "text-muted" : ""}`}>
      <dt className={`min-w-0 ${muted ? "" : "text-muted"} ${strong ? "font-semibold text-foreground" : ""}`}>{label}</dt>
      <dd className={`whitespace-nowrap tabular-nums ${strong ? "font-semibold" : ""}`}>{value}</dd>
    </div>
  );
}

type Props = {
  /** Tháng đang xem (tháng trả). */
  month: string;
  /** "quý 4/2026" — rỗng ở tháng thường. */
  label: string;
  data: SalesTeamQuarter | null | undefined;
};

export function QuarterReview({ month, label, data }: Props) {
  return (
    <section aria-labelledby="xet-quy" className="space-y-2">
      <h3 id="xet-quy" className="text-base font-semibold">
        Xét {label}
      </h3>
      {!data ? (
        <p className="text-sm text-muted">Chưa xét được {label} — còn việc phải xử lý ở đầu thẻ.</p>
      ) : (
        <>
          <p className="text-sm text-muted">
            {monthsText(data.months)} · phần chênh trả ở lương tháng {monthText(month)}. Thưởng vượt từng tháng được tính lại
            theo tỷ lệ vượt của cả quý (mỗi tháng giữ mức chất lượng của tháng đó); cao hơn số đã trả thì trả thêm phần chênh
            — người bán giữ phần của mình, phần túi quý chia theo ngày công cả quý; thấp hơn thì không trừ.
          </p>
          {data.squads.length === 0 ? (
            <EmptyState title={`Chưa có số của tiểu đội nào trong ${label}`} />
          ) : (
            <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
              {data.squads.map((q) => {
                const perPerson = q.topUp > Math.max(0, q.quarterBonus - q.paidBonus);
                return (
                  <li key={q.squadId} className="space-y-2 rounded-md border border-border bg-surface p-3 text-sm">
                    <p className="font-semibold">
                      <SquadMark squadId={q.squadId} name={q.name} size={28} />
                    </p>
                    <dl className="space-y-1">
                      <Row label="Mục tiêu quý" value={formatVND(q.target)} />
                      <Row label="Doanh thu xét mục tiêu" value={formatVND(q.targetRevenue)} />
                      <Row
                        label="Tỷ lệ vượt quý"
                        value={q.overRatioBp > 0 ? percentFromBp(q.overRatioBp) : "Chưa vượt"}
                      />
                    </dl>
                    <dl className="space-y-1 border-t border-border pt-2">
                      <Row label="Thưởng vượt đã trả" value={formatVND(q.paidBonus)} />
                      <Row label="Tính lại theo cả quý" value={formatVND(q.quarterBonus)} />
                      <Row label="Phần chênh trả thêm" value={formatVND(q.topUp)} strong />
                      {q.topUp > 0 ? (
                        <>
                          <Row label="Trong đó vào túi quý" value={formatVND(q.pocket)} />
                          {q.pocketKept > 0 ? <Row label="Túi quý công ty giữ" value={formatVND(q.pocketKept)} muted /> : null}
                        </>
                      ) : null}
                    </dl>
                    {q.overRatioBp === 0 ? (
                      <p className="text-xs text-muted">Cả quý chưa vượt mục tiêu — không có phần chênh.</p>
                    ) : perPerson ? (
                      <p className="text-xs text-muted">
                        Máy so từng người: người dồn doanh số ở tháng vượt thấp vẫn được bù; người đã nhận dư ở tháng vượt
                        cao không bị trừ.
                      </p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
